import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, ArrowRight, Calculator, ClipboardCheck } from 'lucide-react';
import { Seo, Breadcrumbs, MarketingShell, CtaBlock, breadcrumbLd } from '../../seo/kit';
import { PRIMARY_CTA, DEMO_CTA } from '../../seo/data';

const money = (n) => `$${Math.round(n).toLocaleString()}`;

// =============================================================
// A. Draw Schedule Builder
// =============================================================
export function DrawScheduleBuilder() {
  const crumbs = [
    { label: 'Home', href: '/' },
    { label: 'Tools', href: '/tools' },
    { label: 'Draw Schedule Builder', href: '/tools/draw-schedule-builder' },
  ];
  const [budget, setBudget] = useState('500000');
  const [draws, setDraws] = useState('5');
  const [retainage, setRetainage] = useState('10');

  const schedule = useMemo(() => {
    const total = Math.max(0, parseFloat(budget) || 0);
    const n = Math.min(24, Math.max(1, parseInt(draws) || 1));
    const r = Math.min(50, Math.max(0, parseFloat(retainage) || 0)) / 100;
    const per = total / n;
    let heldTotal = 0;
    const rows = Array.from({ length: n }, (_, i) => {
      const gross = per;
      const held = gross * r;
      heldTotal += held;
      return { i: i + 1, gross, funded: gross - held, held };
    });
    return { rows, total, heldTotal, fundedTotal: total - heldTotal };
  }, [budget, draws, retainage]);

  return (
    <MarketingShell>
      <Seo
        title="Free Construction Draw Schedule Builder | DrawReady"
        description="Build a construction draw schedule in seconds — see how much each draw funds after retainage, and where a cash gap could stall your job. Free tool from DrawReady."
        path="/tools/draw-schedule-builder"
        jsonLd={breadcrumbLd(crumbs)}
      />
      <section style={{ background: '#EEF2F8' }}>
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <Breadcrumbs items={crumbs} />
          <div className="flex items-center gap-2 font-semibold text-sm mt-4" style={{ color: '#1E4ED8' }}><Calculator className="w-5 h-5" /> Free tool</div>
          <h1 className="text-4xl md:text-5xl mt-2" style={{ color: '#13294B', fontWeight: 800, letterSpacing: '-0.02em' }}>Construction Draw Schedule Builder</h1>
          <p className="text-lg mt-3 max-w-2xl" style={{ color: '#5B6678' }}>
            Guessing how much to draw and when is how builders run short mid-project — and a funding gap stalls the job and your subs. Map your draws first, then let DrawReady check each one before your lender does.
          </p>
        </div>
      </section>
      <div className="max-w-4xl mx-auto px-5 md:px-8 py-14 md:py-20">

        <div className="grid sm:grid-cols-3 gap-4 mt-8">
          <label className="block">
            <span className="text-sm font-medium text-slate-600">Construction budget</span>
            <div className="mt-1 flex items-center rounded-lg border border-slate-300 px-3">
              <span className="text-slate-400">$</span>
              <input type="number" min="0" value={budget} onChange={(e) => setBudget(e.target.value)} data-testid="dsb-budget" className="w-full py-2.5 px-1 outline-none" />
            </div>
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-600">Number of draws</span>
            <input type="number" min="1" max="24" value={draws} onChange={(e) => setDraws(e.target.value)} data-testid="dsb-draws" className="mt-1 w-full rounded-lg border border-slate-300 py-2.5 px-3 outline-none" />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-600">Retainage %</span>
            <div className="mt-1 flex items-center rounded-lg border border-slate-300 px-3">
              <input type="number" min="0" max="50" value={retainage} onChange={(e) => setRetainage(e.target.value)} data-testid="dsb-retainage" className="w-full py-2.5 px-1 outline-none" />
              <span className="text-slate-400">%</span>
            </div>
          </label>
        </div>

        <div className="mt-8 overflow-hidden rounded-lg border border-slate-200" data-testid="dsb-results">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="text-left font-semibold px-4 py-3">Draw</th>
                <th className="text-right font-semibold px-4 py-3">Work value</th>
                <th className="text-right font-semibold px-4 py-3">Funded now</th>
                <th className="text-right font-semibold px-4 py-3">Held (retainage)</th>
              </tr>
            </thead>
            <tbody>
              {schedule.rows.map((row) => (
                <tr key={row.i} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-medium text-slate-800">Draw {row.i}</td>
                  <td className="px-4 py-3 text-right text-slate-600">{money(row.gross)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-emerald-600">{money(row.funded)}</td>
                  <td className="px-4 py-3 text-right text-amber-600">{money(row.held)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-slate-200 bg-slate-50 font-bold text-slate-900">
                <td className="px-4 py-3">Total</td>
                <td className="px-4 py-3 text-right">{money(schedule.total)}</td>
                <td className="px-4 py-3 text-right text-emerald-700">{money(schedule.fundedTotal)}</td>
                <td className="px-4 py-3 text-right text-amber-700">{money(schedule.heldTotal)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-sm text-slate-500 mt-3">
          <strong className="text-amber-600">{money(schedule.heldTotal)}</strong> stays held back until closeout. Plan working capital to cover that gap — or your final draw comes up short.
        </p>

        <div className="mt-10 rounded-lg bg-[#13294B] text-white p-8">
          <div className="text-xl font-bold">A schedule is only half the battle — the draw still has to pass the bank.</div>
          <p className="text-slate-300 mt-2">DrawReady checks every draw against your lender’s requirements and shows what’s missing before you submit.</p>
          <div className="flex flex-wrap gap-3 mt-5">
            <Link to={PRIMARY_CTA.href} data-testid="dsb-cta-primary" className="inline-flex items-center gap-2 rounded-md bg-[#1E4ED8] hover:bg-[#1740AE] px-6 py-3 font-semibold">{PRIMARY_CTA.label} <ArrowRight className="w-4 h-4" /></Link>
            <Link to={DEMO_CTA.href} className="inline-flex items-center gap-2 rounded-md bg-white/10 hover:bg-white/20 px-6 py-3 font-semibold">{DEMO_CTA.label}</Link>
          </div>
        </div>
      </div>
    </MarketingShell>
  );
}

// =============================================================
// B. Draw Cost / Loan Interest Estimator
// =============================================================
export function DrawInterestEstimator() {
  const crumbs = [
    { label: 'Home', href: '/' },
    { label: 'Tools', href: '/tools' },
    { label: 'Draw Cost Estimator', href: '/tools/draw-cost-estimator' },
  ];
  const [loan, setLoan] = useState('500000');
  const [rate, setRate] = useState('9');
  const [term, setTerm] = useState('12');

  const calc = useMemo(() => {
    const total = Math.max(0, parseFloat(loan) || 0);
    const r = Math.max(0, parseFloat(rate) || 0) / 100;
    const n = Math.min(60, Math.max(1, parseInt(term) || 1));
    // Construction loans charge interest only on the drawn (outstanding) balance.
    // Model an even draw-down: cumulative drawn after month m = total * m/n.
    let phased = 0;
    for (let m = 1; m <= n; m++) phased += (total * (m / n)) * (r / 12);
    // If you drew the full amount on day one and paid interest on all of it:
    const upfront = total * r * (n / 12);
    const saved = Math.max(0, upfront - phased);
    return { total, phased, upfront, saved, avgMonthly: phased / n };
  }, [loan, rate, term]);

  return (
    <MarketingShell>
      <Seo
        title="Free Construction Loan Interest / Draw Cost Estimator | DrawReady"
        description="Estimate the interest cost of your construction loan based on how you draw. See how drawing too much too early quietly eats your budget — free tool from DrawReady."
        path="/tools/draw-cost-estimator"
        jsonLd={breadcrumbLd(crumbs)}
      />
      <section style={{ background: '#EEF2F8' }}>
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <Breadcrumbs items={crumbs} />
          <div className="flex items-center gap-2 font-semibold text-sm mt-4" style={{ color: '#1E4ED8' }}><Calculator className="w-5 h-5" /> Free tool</div>
          <h1 className="text-4xl md:text-5xl mt-2" style={{ color: '#13294B', fontWeight: 800, letterSpacing: '-0.02em' }}>Construction Loan Interest Estimator</h1>
          <p className="text-lg mt-3 max-w-2xl" style={{ color: '#5B6678' }}>
            Every dollar you draw starts the interest clock. Draw too much too early and interest quietly eats into your budget. See what your draws actually cost — then draw only what’s funded and needed, when it’s needed.
          </p>
        </div>
      </section>
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-14 md:py-20">

        <div className="grid sm:grid-cols-3 gap-4 mt-8">
          <label className="block">
            <span className="text-sm font-medium text-slate-600">Loan amount</span>
            <div className="mt-1 flex items-center rounded-lg border border-slate-300 px-3">
              <span className="text-slate-400">$</span>
              <input type="number" min="0" value={loan} onChange={(e) => setLoan(e.target.value)} data-testid="die-loan" className="w-full py-2.5 px-1 outline-none" />
            </div>
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-600">Interest rate (APR)</span>
            <div className="mt-1 flex items-center rounded-lg border border-slate-300 px-3">
              <input type="number" min="0" step="0.1" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="die-rate" className="w-full py-2.5 px-1 outline-none" />
              <span className="text-slate-400">%</span>
            </div>
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-600">Build length (months)</span>
            <input type="number" min="1" max="60" value={term} onChange={(e) => setTerm(e.target.value)} data-testid="die-term" className="mt-1 w-full rounded-lg border border-slate-300 py-2.5 px-3 outline-none" />
          </label>
        </div>

        <div className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="die-results">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-6">
            <div className="text-sm font-medium text-emerald-700">Estimated interest — drawing as you go</div>
            <div className="text-3xl font-black text-emerald-700 mt-1">{money(calc.phased)}</div>
            <div className="text-sm text-emerald-600/80 mt-1">≈ {money(calc.avgMonthly)}/mo average</div>
          </div>
          <div className="rounded-lg border border-red-200 bg-red-50 p-6">
            <div className="text-sm font-medium text-red-700">If you drew it all up front</div>
            <div className="text-3xl font-black text-red-700 mt-1">{money(calc.upfront)}</div>
            <div className="text-sm text-red-600/80 mt-1">interest on the full balance the whole time</div>
          </div>
        </div>
        <p className="text-sm text-slate-500 mt-3">
          Drawing only what you need, when you need it, could save roughly <strong className="text-emerald-600">{money(calc.saved)}</strong> in interest on this build. Construction loans charge interest only on what you’ve drawn — so the pace of your draws is real money.
        </p>

        <div className="mt-10 rounded-lg bg-[#13294B] text-white p-8">
          <div className="text-xl font-bold">Drawing the right amount at the right time is the whole game.</div>
          <p className="text-slate-300 mt-2">DrawReady helps you draw only what’s funded and needed — and checks the draw is bank-ready before your lender sees it.</p>
          <div className="flex flex-wrap gap-3 mt-5">
            <Link to={PRIMARY_CTA.href} data-testid="die-cta-primary" className="inline-flex items-center gap-2 rounded-md bg-[#1E4ED8] hover:bg-[#1740AE] px-6 py-3 font-semibold">{PRIMARY_CTA.label} <ArrowRight className="w-4 h-4" /></Link>
            <Link to={DEMO_CTA.href} className="inline-flex items-center gap-2 rounded-md bg-white/10 hover:bg-white/20 px-6 py-3 font-semibold">{DEMO_CTA.label}</Link>
          </div>
        </div>
        <div className="mt-8"><CtaBlock /></div>
      </div>
    </MarketingShell>
  );
}

// =============================================================
// D. Draw Readiness Checklist
// =============================================================
const CHECK_ITEMS = [
  { id: 'form', label: 'Using the bank’s own draw request form (not a generic one)' },
  { id: 'lines', label: 'Every line item is within its budgeted amount' },
  { id: 'waivers', label: 'A signed lien waiver for every sub being paid' },
  { id: 'receipts', label: 'Receipts / invoices attached and matching the amounts' },
  { id: 'inspection', label: 'Inspection cleared for the work being claimed' },
  { id: 'retainage', label: 'Retainage held back on each line' },
  { id: 'schedule', label: 'Draw amount is within what the schedule allows' },
];

export function DrawReadinessChecklist() {
  const crumbs = [
    { label: 'Home', href: '/' },
    { label: 'Tools', href: '/tools' },
    { label: 'Draw Readiness Checklist', href: '/tools/draw-readiness-checklist' },
  ];
  const [checked, setChecked] = useState({});
  const done = CHECK_ITEMS.filter((i) => checked[i.id]).length;
  const score = Math.round((done / CHECK_ITEMS.length) * 100);
  const ready = score === 100;
  const toggle = (id) => setChecked((p) => ({ ...p, [id]: !p[id] }));

  const verdict = ready
    ? { color: 'emerald', title: 'Bank-ready', msg: 'Every box is checked. This is what a fundable draw looks like.' }
    : score >= 60
      ? { color: 'amber', title: 'Almost — but it’ll get held', msg: 'One missing item is all it takes for the bank to hold the whole draw.' }
      : { color: 'red', title: 'Not ready to submit', msg: 'Submitting now risks a rejection and days of delay while subs wait to be paid.' };

  return (
    <MarketingShell>
      <Seo
        title="Free Construction Draw Readiness Checklist | DrawReady"
        description="Is your construction draw bank-ready? Check the 7 things lenders look for and get an instant readiness score — before a missing document stalls your funding."
        path="/tools/draw-readiness-checklist"
        jsonLd={breadcrumbLd(crumbs)}
      />
      <section style={{ background: '#EEF2F8' }}>
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <Breadcrumbs items={crumbs} />
          <div className="flex items-center gap-2 font-semibold text-sm mt-4" style={{ color: '#1E4ED8' }}><ClipboardCheck className="w-5 h-5" /> Free tool</div>
          <h1 className="text-4xl md:text-5xl mt-2" style={{ color: '#13294B', fontWeight: 800, letterSpacing: '-0.02em' }}>Is your draw bank-ready?</h1>
          <p className="text-lg mt-3 max-w-2xl" style={{ color: '#5B6678' }}>
            One missing document holds your whole draw — and every day it waits, your subs aren’t paid and interest keeps running. Check the 7 things your lender looks for and see where you stand.
          </p>
        </div>
      </section>
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-14 md:py-20">

        <div className={`mt-8 rounded-lg border p-6 ${ready ? 'border-emerald-200 bg-emerald-50' : verdict.color === 'amber' ? 'border-amber-200 bg-amber-50' : 'border-red-200 bg-red-50'}`} data-testid="drc-verdict">
          <div className="flex items-center justify-between">
            <div>
              <div className={`text-2xl font-extrabold ${ready ? 'text-emerald-700' : verdict.color === 'amber' ? 'text-amber-700' : 'text-red-700'}`}>{verdict.title}</div>
              <div className="text-slate-600 mt-1">{verdict.msg}</div>
            </div>
            <div className={`text-4xl font-black ${ready ? 'text-emerald-600' : verdict.color === 'amber' ? 'text-amber-600' : 'text-red-600'}`} data-testid="drc-score">{score}%</div>
          </div>
        </div>

        <div className="mt-6 space-y-2">
          {CHECK_ITEMS.map((item) => (
            <button
              key={item.id}
              onClick={() => toggle(item.id)}
              data-testid={`drc-item-${item.id}`}
              className={`w-full flex items-center gap-3 text-left rounded-xl border px-4 py-3.5 transition-colors ${checked[item.id] ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}
            >
              {checked[item.id] ? <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" /> : <Circle className="w-5 h-5 text-slate-300 flex-shrink-0" />}
              <span className={checked[item.id] ? 'text-slate-800' : 'text-slate-600'}>{item.label}</span>
            </button>
          ))}
        </div>

        <div className="mt-10 rounded-lg bg-[#13294B] text-white p-8">
          <div className="text-xl font-bold">Checking it by hand is easy to get wrong. Let DrawReady check it for real.</div>
          <p className="text-slate-300 mt-2">DrawReady scores your actual draw against your lender’s requirements and flags every gap before it reaches them.</p>
          <div className="flex flex-wrap gap-3 mt-5">
            <Link to={PRIMARY_CTA.href} data-testid="drc-cta-primary" className="inline-flex items-center gap-2 rounded-md bg-[#1E4ED8] hover:bg-[#1740AE] px-6 py-3 font-semibold">{PRIMARY_CTA.label} <ArrowRight className="w-4 h-4" /></Link>
            <Link to={DEMO_CTA.href} className="inline-flex items-center gap-2 rounded-md bg-white/10 hover:bg-white/20 px-6 py-3 font-semibold">{DEMO_CTA.label}</Link>
          </div>
        </div>
        <div className="mt-8"><CtaBlock /></div>
      </div>
    </MarketingShell>
  );
}
